/**
 * Who may administer a dashboard, and when its lock applies.
 *
 * The creator of a public dashboard can lock it, which freezes its layout and
 * its settings for everyone else. Super-admins have always been allowed past
 * that lock; BBSW-10846 gives managers the same reach, so they can rename any
 * dashboard, manage its subscribers and change its settings even when they do
 * not own it. Every other role is unchanged: the lock still applies to them,
 * and the `manage_dashboards` permission is still required on top of this.
 */

type DashboardAuth = {
  id?: string | null
  roles?: string[] | null
}

type LockableDashboard = {
  creator_id?: string | null
  locked?: boolean | number | null
}

/** Roles allowed to administer a dashboard they do not own. */
export const DASHBOARD_ADMINISTRATOR_ROLES = ['super-admin', 'manager']

/**
 * True when the user holds a role that administers every dashboard of the
 * tenant, owned or not.
 */
export function canAdministerAnyDashboard(
  auth: DashboardAuth | null | undefined
): boolean {
  const roles = auth?.roles ?? []

  return roles.some((role) => DASHBOARD_ADMINISTRATOR_ROLES.includes(role))
}

/**
 * True when the dashboard's lock closes it for this user: the dashboard is
 * locked, it belongs to someone else, and the user administers no dashboard
 * but their own. An unlocked dashboard is never closed by this rule.
 */
export function isDashboardLockedFor(
  auth: DashboardAuth | null | undefined,
  dashboard: LockableDashboard | null | undefined
): boolean {
  if (!dashboard?.locked) {
    return false
  }

  if (canAdministerAnyDashboard(auth)) {
    return false
  }

  return auth?.id !== dashboard?.creator_id
}
